import { useCallback, useEffect, useRef, useState } from 'react'

export type LayoutState = {
  width: number
  height: number
  x: number
  y: number
}

type UseLayoutOptions = {
  /**
   * If true, the layout will not be updated after the first measurement.
   */
  isSticky?: boolean
}

const useLayout = ({ isSticky = false }: UseLayoutOptions = {}): [
  LayoutState,
  { ref(node: HTMLElement | null): void },
] => {
  const [layout, setLayout] = useState<LayoutState>({
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  })

  const hasMeasured = useRef(false)
  const observerRef = useRef<ResizeObserver | null>(null)

  const ref = useCallback(
    (node: HTMLElement | null) => {
      if (!node) return

      const measure = () => {
        if (isSticky && hasMeasured.current) return

        const rect = node.getBoundingClientRect()

        if (rect.height === layout.height && rect.width === layout.width) return

        setLayout({
          height: rect.height,
          width: rect.width,
          x: rect.left + window.scrollX,
          y: rect.top + window.scrollY,
        })

        hasMeasured.current = true
      }

      measure()

      observerRef.current = new ResizeObserver(measure)
      observerRef.current.observe(node)

      node.addEventListener('animationend', measure)
      node.addEventListener('transitionend', measure)

      return () => {
        node.removeEventListener('animationend', measure)
        node.removeEventListener('transitionend', measure)
      }
    },
    [isSticky, layout.height, layout.width],
  )

  useEffect(() => {
    return () => observerRef.current?.disconnect()
  }, [])

  return [layout, { ref }]
}

export default useLayout
